/**
 * HistoryTab — displays a chronological feed of view events.
 * Used inside the Status drawer as a tab.
 */

import { useRef, useCallback } from 'react';
import { List, Segmented, Spin, Empty, Tooltip, Typography } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import { MasterPowerLogoIcon } from '@/components/MasterPowerLogoIcon';
import { View } from '@/types';
import { HistoryEvent, HistoryEventType } from '@/types/history';
import { useViewHistory } from '@/hooks/useViewHistory';
import { formatHistoryEvent } from '@/utils/historyMessages';
import { formatTimeAgo } from '@/utils/dateUtils';

const { Text } = Typography;

const FILTER_OPTIONS = [
  { label: (<span style={{ fontSize: 14 }}>All</span>), value: 'all' },
  { label: (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}><MaterialIcon name="person" size={22} /> Actions</span>), value: 'user_action' },
  { label: (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}><MaterialIcon name="monitor_heart" size={22} /> System</span>), value: 'state_change' },
];

// Ant Design CSS variable tokens for semantic colors
const COLOR_SUCCESS = 'var(--ant-color-success)';
const COLOR_WARNING = 'var(--ant-color-warning)';
const COLOR_ERROR = 'var(--ant-color-error)';
const COLOR_PRIMARY = 'var(--ant-color-primary)';

// Per-event icon + color, reusing icons from missionCriticalConfig / StatusDashboard / StatusTab.
// `svg: true` renders the MasterPower brand mark instead of a Material symbol; `color` then
// tints the status dot next to it (the brand mark keeps its fixed black/yellow colors).
const ACTION_ICON_CONFIG: Record<string, { icon: string; color: string; svg?: boolean }> = {
  // User actions
  capturing_started:    { icon: 'play_circle',    color: COLOR_SUCCESS },
  capturing_stopped:    { icon: 'pause_circle',   color: COLOR_WARNING },
  interval_changed:     { icon: 'timer',          color: COLOR_PRIMARY },
  schedule_changed:     { icon: 'calendar_clock',  color: COLOR_PRIMARY },
  camera_settings_changed: { icon: 'tune',        color: COLOR_PRIMARY },
  test_shot_requested:  { icon: 'photo',          color: COLOR_PRIMARY },
  // State changes
  test_shot_completed:  { icon: 'photo',          color: COLOR_SUCCESS },
  test_shot_failed:     { icon: 'hide_image',     color: COLOR_ERROR },
  view_renamed:         { icon: 'edit',           color: COLOR_PRIMARY },
  camera_connected:     { icon: 'photo_camera',   color: COLOR_SUCCESS },
  camera_disconnected:  { icon: 'no_photography', color: COLOR_ERROR },
  device_online:        { icon: 'cloud_done',     color: COLOR_SUCCESS },
  device_offline:       { icon: 'cloud_off',      color: COLOR_ERROR },
  battery_low:          { icon: 'battery_alert',  color: COLOR_WARNING },
  battery_restored:     { icon: 'battery_full',   color: COLOR_SUCCESS },
  battery_temperature_warning:  { icon: 'thermostat', color: COLOR_WARNING },
  battery_temperature_critical: { icon: 'thermostat', color: COLOR_ERROR },
  battery_temperature_resolved: { icon: 'thermostat', color: COLOR_SUCCESS },
  charger_connected:    { icon: 'power',          color: COLOR_SUCCESS },
  charger_disconnected: { icon: 'power_off',      color: COLOR_ERROR },
  app_updated:          { icon: 'system_update',  color: COLOR_SUCCESS },
  // MasterPower — render the brand mark; color tints the status dot
  master_power_connected:    { icon: 'bolt',  color: COLOR_SUCCESS, svg: true },
  master_power_disconnected: { icon: 'bolt',  color: COLOR_ERROR,   svg: true },
  master_power_cycle:        { icon: 'bolt',  color: COLOR_WARNING, svg: true },
  // Errors (from Android)
  capture_failed:       { icon: 'hide_image',     color: COLOR_ERROR },
  upload_failed:        { icon: 'cloud_off',      color: COLOR_ERROR },
  // Cloud Sync (from export-worker)
  cloud_sync_paused:       { icon: 'cloud_sync', color: COLOR_WARNING },
  cloud_sync_resumed:      { icon: 'cloud_sync', color: COLOR_SUCCESS },
  cloud_sync_photo_failed: { icon: 'cloud_off',  color: COLOR_ERROR },
};

const FALLBACK_ICON = { icon: 'info', color: COLOR_PRIMARY };

function getEventIcon(event: HistoryEvent): { icon: string; color: string; svg?: boolean } {
  // raw_toggled has a conditional icon based on details.enabled
  if (event.action === 'raw_toggled') {
    return { icon: event.details?.enabled ? 'raw_on' : 'raw_off', color: COLOR_PRIMARY };
  }
  return ACTION_ICON_CONFIG[event.action] || FALLBACK_ICON;
}

interface HistoryTabProps {
  view: View;
  filterType: HistoryEventType | 'all';
  onFilterChange: (filter: HistoryEventType | 'all') => void;
}

export function HistoryTab({ view, filterType, onFilterChange }: HistoryTabProps) {
  const { events, loading, hasMore, loadMore, loadingMore } = useViewHistory(
    view.userId,
    view.projectId,
    view.viewId,
    filterType
  );

  const listRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;
      if (nearBottom && hasMore && !loadingMore) {
        loadMore();
      }
    },
    [hasMore, loadMore, loadingMore]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Segmented
        className="segmented-emphasis"
        options={FILTER_OPTIONS}
        value={filterType}
        onChange={(val) => onFilterChange(val as HistoryEventType | 'all')}
        block
      />

      <Text type="secondary" style={{ fontSize: 11 }}>
        Device time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
      </Text>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : events.length === 0 ? (
        <Empty description="No events yet" />
      ) : (
        <div
          ref={listRef}
          onScroll={handleScroll}
          style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}
        >
          <List
            dataSource={events}
            renderItem={(event) => {
              const config = getEventIcon(event);
              const eventDate = event.timestamp?.toDate?.();
              const timeAgo = eventDate ? formatTimeAgo(eventDate) : '';
              const absoluteTime = eventDate
                ? eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                : '';
              const dayLabel = eventDate ? (() => {
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                const diffDays = Math.round((today.getTime() - eventDay.getTime()) / 86400000);
                if (diffDays === 0) return 'Today';
                if (diffDays === 1) return 'Yesterday';
                const dow = eventDate.toLocaleDateString('en', { weekday: 'short' }).toUpperCase();
                const day = eventDate.getDate();
                const mon = eventDate.toLocaleDateString('en', { month: 'short' });
                return `${dow} ${day} ${mon}`;
              })() : '';
              const fullDateTime = eventDate ? eventDate.toLocaleString() : '';

              const isAppUpdated = event.action === 'app_updated';

              return (
                <List.Item style={{
                  padding: '8px 0',
                  alignItems: 'flex-start',
                  ...(isAppUpdated ? {
                    background: 'var(--ant-color-success-bg)',
                    borderRadius: 6,
                    padding: '8px 10px',
                  } : {}),
                }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%' }}>
                    {config.svg ? (
                      // MasterPower brand mark with a status dot to keep the success/error/warning signal
                      <div style={{ position: 'relative', marginTop: 2, flexShrink: 0, width: 20, height: 20 }}>
                        <MasterPowerLogoIcon size={20} />
                        <span style={{
                          position: 'absolute',
                          right: -2,
                          bottom: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: config.color,
                          border: '1.5px solid var(--ant-color-bg-container)',
                        }} />
                      </div>
                    ) : (
                      <MaterialIcon
                        name={config.icon}
                        size={20}
                        style={{ color: config.color, marginTop: 2, flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ display: 'block', whiteSpace: 'pre-line' }}>
                        {formatHistoryEvent(event)}
                      </Text>
                      <Tooltip title={`${fullDateTime} (${timeAgo})`}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayLabel} · {absoluteTime}
                        </Text>
                      </Tooltip>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin size="small" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
